import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Drawer, IconButton, Typography, Tabs, Tab, Box, Popper, Tooltip } from '@mui/material'
import { X, Search, ChevronDown, Plus, ArrowRight, AlertCircle } from 'lucide-react'
import Button from '../ui/Button'
import { AvailabilityBadge } from './AvailabilityBadge'
import { showConfirmModal, showErrorModal } from '../ui/ConfirmDialog'
import { Condition, fetchConditions } from '../../services/conditionsService'
import { IfNode, TextRuleNode, RuleConfig, ConditionGroup, ConditionEntry, CodeItemGroup } from '../../types/codeIndex'
import { validateRuleValueReferences } from '../../utils/codeIndexUtils'
import { normalizeCodeItemReference } from '../../utils/codeReference'
import { isExclusionOperator, validateRuleLogic, type RuleValidationError, type RuleValidationWarning } from '../../utils/ruleValidation'

type RuleCodeItemData = {
  code: string
  options: string[]
  rows?: Array<{
    optionCode: string
    group?: string | null
    valuePosition?: number | null
    valuePositionEnd?: number | null
  }>
  composite?: boolean
  subgroups?: Array<{
    name: string
    start: number
    end: number
    options: string[]
  }>
}

// -----------------------------
// Props Interface
// -----------------------------
export interface ConditionConfigDrawerProps {
  open: boolean
  onClose: () => void
  onApply: (config: RuleConfig) => Promise<void> | void
  initialConfig?: RuleConfig | null
  availableReferences?: string[]
  currentCodeItem?: string
  codeItemsData?: RuleCodeItemData[]
  ruleValidationContext?: {
    groups: CodeItemGroup[]
    targetGroupIndex: number
    targetRowIndex: number
  }
  // Shown above the conditions, e.g. where a proposed rule came from and who must confirm it.
  notice?: string | null
}

const CONDITIONS_TAB_EXTRA_OPERATORS: Condition[] = [
  { value: 'between', displayName: 'between' },
  { value: 'startsWith', displayName: 'starts with' },
  { value: 'endsWith', displayName: 'ends with' },
  { value: 'contains', displayName: 'contains' },
]

const CONDITIONS_TAB_EXTRA_OPERATOR_VALUES = new Set(
  CONDITIONS_TAB_EXTRA_OPERATORS.map(condition => condition.value)
)

export function mergeConditionsTabOperators(conditions: Condition[]): Condition[] {
  const mergedConditions = [...conditions]

  CONDITIONS_TAB_EXTRA_OPERATORS.forEach(extraCondition => {
    if (!mergedConditions.some(condition => condition.value === extraCondition.value)) {
      mergedConditions.push(extraCondition)
    }
  })

  return mergedConditions
}

function conditionOperatorToDisplayValue(condition: string, conditions: Condition[] = []): string {
  if (condition === '==') return '='
  if (CONDITIONS_TAB_EXTRA_OPERATOR_VALUES.has(condition)) {
    return conditions.find(item => item.value === condition)?.displayName || condition
  }
  return condition
}

function isBetweenConditionEntry(entry: Pick<ConditionEntry, 'condition' | 'from' | 'to'>): boolean {
  return entry.condition === 'between'
}

function hasConditionEntrySingleValue(entry: Pick<ConditionEntry, 'value'>): boolean {
  return Boolean(String(entry.value || '').trim())
}

function hasConditionEntryPositionValues(entry: Pick<ConditionEntry, 'values'>): boolean {
  return Object.entries(entry.values || {}).some(([position, value]) => (
    Number.isInteger(Number(position)) && Number(position) >= 0 && Boolean(String(value || '').trim())
  ))
}

function hasConditionEntryRangeValues(entry: Pick<ConditionEntry, 'from' | 'to'>): boolean {
  return Boolean(String(entry.from || '').trim() && String(entry.to || '').trim())
}

function isConditionEntryValid(entry: ConditionEntry): boolean {
  if (!entry.reference || !entry.condition) return false

  if (entry.values != null) {
    return entry.condition === '==' && hasConditionEntryPositionValues(entry)
  }

  if (isBetweenConditionEntry(entry)) {
    return hasConditionEntryRangeValues(entry)
  }

  return hasConditionEntrySingleValue(entry)
}

function formatConditionReference(reference: string): string {
  if (!reference) return ''
  return /^code\s+/i.test(reference) ? reference : `Code ${reference}`
}

function tryFormatPositionableConditionGroup(
  group: ConditionGroup,
  codeItemsData: RuleCodeItemData[]
): string | null {
  const entries: ConditionEntry[] = []
  const collect = (current: ConditionGroup): boolean => {
    for (const entry of current.conditions || []) {
      if (!hasConditionEntryPositionValues(entry)) return false
      entries.push(entry)
    }
    return (current.groups || []).every(collect)
  }
  if (!collect(group) || entries.length === 0) return null

  const reference = entries[0].reference
  if (!reference || entries.some(entry => normalizeCodeItemReference(entry.reference) !== normalizeCodeItemReference(reference))) return null
  const referencedItem = codeItemsData.find(item => normalizeCodeItemReference(item.code) === normalizeCodeItemReference(reference))
  const selections = new Map<string, string[]>()

  entries.forEach(entry => {
    const values = Object.entries(entry.values || {})
      .map(([position, value]) => ({ position: Number(position), value: String(value || '') }))
      .filter(item => Number.isInteger(item.position) && item.position >= 0 && item.value)
      .sort((left, right) => left.position - right.position)
    if (values.length === 0) return
    const start = values[0].position
    const end = values[values.length - 1].position
    const subgroup = referencedItem?.subgroups?.find(item => item.start === start && item.end === end)
    const positionLabel = start === end ? `Pos ${start + 1}` : `Pos ${start + 1}-${end + 1}`
    const label = subgroup?.name ? `${subgroup.name} ${positionLabel}` : positionLabel
    const fragment = values.map(item => item.value).join('')
    selections.set(label, Array.from(new Set([...(selections.get(label) || []), fragment])))
  })

  if (selections.size === 0) return null
  return `${formatConditionReference(reference)}: ${Array.from(selections.entries())
    .map(([label, values]) => `${label} = ${values.join(' or ')}`)
    .join('; ')}`
}

function getConditionDropdownPrimaryLabel(condition: Condition): string {
  if (condition.value === 'between') return 'A...B'
  if (condition.value === 'startsWith') return 'ABC*'
  if (condition.value === 'endsWith') return '*ABC'
  if (condition.value === 'contains') return '*ABC*'
  return condition.value
}

function getConditionDropdownSecondaryLabel(condition: Condition): string {
  return `(${condition.displayName})`
}

function formatConditionEntryDescription(entry: ConditionEntry, conditions: Condition[] = []): string {
  if (hasConditionEntryPositionValues(entry)) {
    const positionedValues = Object.entries(entry.values || {})
      .map(([position, value]) => ({ position: Number(position), value: String(value || '') }))
      .filter(item => Number.isInteger(item.position) && item.position >= 0 && item.value)
      .sort((left, right) => left.position - right.position)
      .map(item => `pos ${item.position + 1} = ${item.value}`)
      .join(', ')
    return `${formatConditionReference(entry.reference)}: ${positionedValues}`
  }

  if (isBetweenConditionEntry(entry)) {
    return `${formatConditionReference(entry.reference)} ${conditionOperatorToDisplayValue(entry.condition, conditions)} ${entry.from} and ${entry.to}`
  }

  return `${formatConditionReference(entry.reference)} ${conditionOperatorToDisplayValue(entry.condition, conditions)} ${String(entry.value || '')}`
}

function getRuleValidationIssueSummary(issue: RuleValidationError | RuleValidationWarning): string {
  if (issue.type === 'own-position') return 'This rule constrains the position its own value occupies. Review the highlighted condition.'
  if (issue.type === 'impossible') return 'This rule can never be satisfied. Review the highlighted conditions.'
  if (issue.type === 'conflict') return 'Conflicting conditions detected. Review the highlighted conditions.'
  if (issue.type === 'range') return 'Invalid range bounds. Review the highlighted condition.'
  if (issue.type === 'dead-trigger') return 'This rule can never apply to this value. Review this rule before saving.'
  if (issue.type === 'dead-alternative') return 'A value this rule names can never hold. Review the highlighted condition.'
  return 'Numeric comparison warning. Review this rule before saving.'
}

function formatConditionValues(values: string[]): string {
  return values.join(' or ')
}

function formatOrConditionEntries(entries: ConditionEntry[], conditions: Condition[] = []): string {
  const groupedEntries = new Map<string, ConditionEntry[]>()

  entries.forEach((entry, index) => {
    const key = isBetweenConditionEntry(entry)
      ? `between-${index}`
      : hasConditionEntryPositionValues(entry)
      ? `positionable-${index}`
      : `${entry.reference}|||${entry.condition}`
    const existingEntries = groupedEntries.get(key)
    if (existingEntries) {
      existingEntries.push(entry)
    } else {
      groupedEntries.set(key, [entry])
    }
  })

  return Array.from(groupedEntries.values()).map(group => {
    if (group.length === 1) {
      return formatConditionEntryDescription(group[0], conditions)
    }

    const [firstEntry] = group
    const values = formatConditionValues(group.map(entry => String(entry.value || '')))
    return `${formatConditionReference(firstEntry.reference)} ${conditionOperatorToDisplayValue(firstEntry.condition, conditions)} ${values}`
  }).join(' OR ')
}

function isSimpleConditionGroup(group: ConditionGroup | null | undefined): boolean {
  if (!group || (group.logicalOperator !== 'OR' && group.logicalOperator !== 'AND')) return false
  if ((group.groups || []).length > 0) return false

  const conditions = group.conditions || []
  if (conditions.length === 0) return group.logicalOperator === 'OR'
  if (conditions.some(condition => isBetweenConditionEntry(condition))) return false

  const [firstCondition] = conditions
  // An AND group is a single clause only when it excludes several values of the same Code Item.
  if (group.logicalOperator === 'AND' && !isExclusionOperator(firstCondition.condition)) return false

  return conditions.every(condition =>
    (condition.reference || '') === (firstCondition.reference || '') &&
    (condition.condition || '') === (firstCondition.condition || '')
  )
}

/**
 * Values excluded together are stored as an AND of `!=` entries; they belong to one clause and read
 * as `Code 02 != D and C`.
 */
function formatAndConditionEntries(entries: ConditionEntry[], conditions: Condition[] = []): string[] {
  const parts: string[] = []
  const partIndexByKey = new Map<string, number>()
  const valuesByKey = new Map<string, string[]>()

  entries.forEach((entry, index) => {
    const mergeable = isExclusionOperator(entry.condition)
      && !isBetweenConditionEntry(entry)
      && !hasConditionEntryPositionValues(entry)
    if (!mergeable) {
      parts.push(formatConditionEntryDescription(entry, conditions))
      return
    }

    const key = `${normalizeCodeItemReference(entry.reference)}|||${entry.condition}`
    const values = [...(valuesByKey.get(key) || []), String(entry.value || '')]
    valuesByKey.set(key, values)

    const text = `${formatConditionReference(entry.reference)} ${conditionOperatorToDisplayValue(entry.condition, conditions)} ${values.join(' and ')}`
    const partIndex = partIndexByKey.get(key)
    if (partIndex === undefined) {
      partIndexByKey.set(key, parts.length)
      parts.push(text)
      return
    }

    parts[partIndex] = text
  })

  return parts
}

function generateConditionGroupDescription(
  group: ConditionGroup | null | undefined,
  conditions: Condition[] = [],
  codeItemsData: RuleCodeItemData[] = []
): string {
  if (!group) return ''

  const positionableDescription = tryFormatPositionableConditionGroup(group, codeItemsData)
  if (positionableDescription) return positionableDescription

  const validConditions = (group.conditions || []).filter(entry => isConditionEntryValid(entry))
  const resolvedNestedGroups = (group.groups || []).map(subgroup => ({
    subgroup,
    text: generateConditionGroupDescription(subgroup, conditions, codeItemsData)
  })).filter(entry => Boolean(entry.text))

  const shouldWrapNestedDescriptions = validConditions.length > 0 || resolvedNestedGroups.length > 1
  const nestedDescriptions = resolvedNestedGroups.map(({ subgroup, text }) => {
    if (!text) return ''
    if (!shouldWrapNestedDescriptions || isSimpleConditionGroup(subgroup)) return text
    return `(${text})`
  }).filter(Boolean)

  if (group.logicalOperator === 'OR') {
    const parts: string[] = []
    if (validConditions.length > 0) {
      parts.push(formatOrConditionEntries(validConditions, conditions))
    }
    parts.push(...nestedDescriptions)
    return parts.join(' OR ')
  }

  const parts = [
    ...formatAndConditionEntries(validConditions, conditions),
    ...nestedDescriptions
  ]

  return parts.join(' AND ')
}

function validateConditionGroup(group: ConditionGroup | null | undefined): boolean {
  if (!group) return false
  if (group.logicalOperator !== 'AND' && group.logicalOperator !== 'OR') return false

  const conditions = group.conditions || []
  const groups = group.groups || []

  const hasAnyCondition = conditions.length > 0
  const hasAnyGroup = groups.length > 0
  if (!hasAnyCondition && !hasAnyGroup) return false

  const allConditionsValid = conditions.every(entry => isConditionEntryValid(entry))
  if (!allConditionsValid) return false

  const allGroupsValid = groups.every(subgroup => validateConditionGroup(subgroup))
  if (!allGroupsValid) return false

  return true
}

function hasConfiguredConditionGroup(group: ConditionGroup | null | undefined): boolean {
  if (!group) return false

  const hasConfiguredCondition = (group.conditions || []).some(entry =>
    Boolean(entry.reference || entry.value || entry.from || entry.to || hasConditionEntryPositionValues(entry))
  )

  if (hasConfiguredCondition) return true

  return (group.groups || []).some(subgroup => hasConfiguredConditionGroup(subgroup))
}

function normalizeConsequenceGroups(groups: ConditionGroup[] | null | undefined): ConditionGroup[] | null {
  if (!Array.isArray(groups)) return null
  const normalizedGroups = groups.filter(Boolean)
  return normalizedGroups.length > 0 ? normalizedGroups : null
}

function hasConfiguredConsequenceGroups(groups: ConditionGroup[] | null | undefined): boolean {
  return (groups || []).some(group => hasConfiguredConditionGroup(group))
}

function validateConsequenceGroups(groups: ConditionGroup[] | null | undefined): boolean {
  const configuredGroups = (groups || []).filter(group => hasConfiguredConditionGroup(group))
  if (configuredGroups.length === 0) return false
  return configuredGroups.every(group => validateConditionGroup(group))
}

function generateStructuredIfDescription(rule: IfNode, conditions: Condition[], codeItemsData: RuleCodeItemData[] = []): string {
  const triggerDescription = generateConditionGroupDescription(rule.conditionGroup, conditions, codeItemsData)
  const consequenceDescriptions = (rule.consequenceGroups || [])
    .map(group => generateConditionGroupDescription(group, conditions, codeItemsData))
    .filter(Boolean)

  const lines: string[] = []

  if (triggerDescription) {
    lines.push(`IF ${triggerDescription}`)
  }

  if (consequenceDescriptions.length > 0) {
    lines.push('THEN')
    lines.push(...consequenceDescriptions)
  }

  const structuredDescription = lines.join('\n')
  if (!rule.future) return structuredDescription
  if (!structuredDescription) return FUTURE_REFERENCE
  return `${structuredDescription}; ${FUTURE_REFERENCE}`
}

const dropdownPopperModifiers = [
  {
    name: 'offset',
    options: {
      offset: [0, 6]
    }
  },
  {
    name: 'preventOverflow',
    options: {
      padding: 12
    }
  },
  {
    name: 'flip',
    options: {
      padding: 12
    }
  }
]

// -----------------------------
// Reference Selector Component
// -----------------------------
interface ReferenceSelectorProps {
  value: string
  onChange: (value: string) => void
  availableReferences: string[]
  placeholder?: string
  disabled?: boolean
  compact?: boolean
}

const ReferenceSelector: React.FC<ReferenceSelectorProps> = ({
  value,
  onChange,
  availableReferences,
  placeholder = 'Select reference',
  disabled = false,
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const filteredRefs = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return availableReferences
    return availableReferences.filter(ref => ref.toLowerCase().includes(q))
  }, [availableReferences, searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-1 border rounded bg-white ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-[#1D74D8] cursor-pointer'
          } ${isOpen ? 'border-[#1D74D8] ring-1 ring-[#1D74D8]' : 'border-gray-300'}`}
      >
        <span className={`flex-1 truncate text-left ${value ? 'text-gray-900' : 'text-gray-400'}`}>{value || placeholder}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={dropdownPopperModifiers}
        sx={{ zIndex: 1600, width: containerRef.current?.offsetWidth }}
      >
        <div ref={menuRef} className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#1D74D8]"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredRefs.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            ) : (
              filteredRefs.map(ref => (
                <button
                  key={ref}
                  type="button"
                  onClick={() => {
                    onChange(ref)
                    setIsOpen(false)
                    setSearchQuery('')
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-blue-50 ${value === ref ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'
                    }`}
                >
                  <span>{ref}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
}

// -----------------------------
// Condition Selector Component
// -----------------------------
interface ConditionSelectorProps {
  value: string
  onChange: (value: string) => void
  conditions: Condition[]
  placeholder?: string
  disabled?: boolean
  compact?: boolean
}

const ConditionSelector: React.FC<ConditionSelectorProps> = ({
  value,
  onChange,
  conditions,
  placeholder = 'Condition',
  disabled = false,
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const orderedConditions = useMemo(() => {
    const defaultCondition = conditions.find(condition => condition.value === '==')
    if (!defaultCondition) return conditions

    return [
      defaultCondition,
      ...conditions.filter(condition => condition.value !== '==')
    ]
  }, [conditions])

  const selectedCondition = conditions.find(c => c.value === value)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-1 border rounded bg-white ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-[#1D74D8] cursor-pointer'
          } ${isOpen ? 'border-[#1D74D8] ring-1 ring-[#1D74D8]' : 'border-gray-300'}`}
      >
        <span className={`flex-1 truncate text-left ${value ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedCondition ? selectedCondition.value : placeholder}
        </span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={dropdownPopperModifiers}
        sx={{ zIndex: 1600, width: Math.max(containerRef.current?.offsetWidth || 0, 180) }}
      >
        <div ref={menuRef} className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto min-w-[180px]">
          {orderedConditions.map(cond => (
            <button
              key={cond.value}
              type="button"
              onClick={() => {
                onChange(cond.value)
                setIsOpen(false)
              }}
              className={`w-full flex items-center gap-1.5 px-3 py-2 text-sm text-left hover:bg-blue-50 ${value === cond.value ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'
                }`}
            >
              <span className="font-mono min-w-[44px] flex-shrink-0 whitespace-nowrap">{getConditionDropdownPrimaryLabel(cond)}</span>
              <span className="text-gray-500 text-xs whitespace-nowrap">{getConditionDropdownSecondaryLabel(cond)}</span>
            </button>
          ))}
        </div>
      </Popper>
    </div>
  )
}

// -----------------------------
// Value Input
// -----------------------------
interface ValueInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  compact?: boolean
}

const ValueInput: React.FC<ValueInputProps> = ({ value, onChange, placeholder = '', compact = false }) => {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full border border-gray-300 rounded focus:outline-none focus:border-[#1D74D8] focus:ring-1 focus:ring-[#1D74D8] ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'}`}
      placeholder={placeholder}
    />
  )
}

// -----------------------------
// Value Selector Component (for reference options)
// -----------------------------
interface ValueSelectorProps {
  value: string
  onChange: (value: string) => void
  options: string[]
  placeholder?: string
  disabled?: boolean
  compact?: boolean
  allowFreeTextWhenNoOptions?: boolean
}

const ValueSelector: React.FC<ValueSelectorProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select value',
  disabled = false,
  compact = false,
  allowFreeTextWhenNoOptions = true
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const filteredOptions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return options
    return options.filter(opt => opt.toLowerCase().includes(q))
  }, [options, searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  if (options.length === 0 && allowFreeTextWhenNoOptions) {
    return <ValueInput value={value} onChange={onChange} placeholder={placeholder} compact={compact} />
  }

  if (options.length === 0) {
    return (
      <input
        type="text"
        value={value}
        readOnly
        disabled
        className={`w-full border border-gray-300 rounded bg-gray-100 text-gray-400 cursor-not-allowed ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'}`}
        placeholder={placeholder}
      />
    )
  }

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-1 border rounded bg-white ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} ${
          disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-[#1D74D8] cursor-pointer'
        } ${isOpen ? 'border-[#1D74D8] ring-1 ring-[#1D74D8]' : 'border-gray-300'}`}
      >
        <span className={`flex-1 truncate text-left ${value ? 'text-gray-900' : 'text-gray-400'}`}>{value || placeholder}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={dropdownPopperModifiers}
        sx={{ zIndex: 1600, width: containerRef.current?.offsetWidth }}
      >
        <div ref={menuRef} className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          {options.length > 5 && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#1D74D8]"
                  autoFocus
                />
              </div>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            ) : (
              filteredOptions.map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    onChange(opt)
                    setIsOpen(false)
                    setSearchQuery('')
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-blue-50 ${
                    value === opt ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'
                  }`}
                >
                  <span>{opt}</span>
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
}

// -----------------------------
// Multi Value Selector (referenced-only)
// -----------------------------
interface MultiValueSelectorProps {
  values: string[]
  onChange: (values: string[]) => void
  options: string[]
  placeholder?: string
  disabled?: boolean
  compact?: boolean
}

const MultiValueSelector: React.FC<MultiValueSelectorProps> = ({
  values,
  onChange,
  options,
  placeholder = 'Select values',
  disabled = false,
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const mergedOptions = useMemo(() => {
    const missingValues = values.filter(value => !options.includes(value))
    return [...options, ...missingValues]
  }, [options, values])

  const filteredOptions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim()
    if (!q) return mergedOptions
    return mergedOptions.filter(option => option.toLowerCase().includes(q))
  }, [mergedOptions, searchQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!menuRef.current || !menuRef.current.contains(target))
      ) {
        setIsOpen(false)
        setSearchQuery('')
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const toggleValue = (value: string) => {
    if (values.includes(value)) {
      onChange(values.filter(item => item !== value))
      return
    }
    onChange([...values, value])
  }

  const displayText = values.length > 0 ? values.join(', ') : placeholder

  return (
    <div ref={containerRef} className="relative flex-1 min-w-0">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-1 border rounded bg-white ${compact ? 'px-2 py-1 text-xs' : 'px-2 py-1.5 text-sm'} ${
          disabled ? 'bg-gray-100 cursor-not-allowed opacity-60' : 'hover:border-[#1D74D8] cursor-pointer'
        } ${isOpen ? 'border-[#1D74D8] ring-1 ring-[#1D74D8]' : 'border-gray-300'}`}
      >
        <span className={`flex-1 truncate text-left ${values.length > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{displayText}</span>
        <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      <Popper
        open={isOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        modifiers={dropdownPopperModifiers}
        sx={{ zIndex: 1600, width: containerRef.current?.offsetWidth }}
      >
        <div ref={menuRef} className="bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
          {mergedOptions.length > 5 && (
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#1D74D8]"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">No results</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleValue(option)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left hover:bg-blue-50 ${
                    values.includes(option) ? 'bg-blue-50 text-[#1D74D8]' : 'text-gray-700'
                  }`}
                >
                  <span>{option}</span>
                  <input
                    type="checkbox"
                    readOnly
                    checked={values.includes(option)}
                    className="h-4 w-4 pointer-events-none"
                  />
                </button>
              ))
            )}
          </div>
        </div>
      </Popper>
    </div>
  )
}

function createEmptyIfRule(): IfNode {
  return {
    type: 'if',
    reference: '',
    condition: '==',
    value: '',
    returnValue: undefined,
    conditionGroup: null,
    consequenceGroups: null,
    future: false
  }
}

function createEmptyTextRule(): TextRuleNode {
  return {
    type: 'text',
    description: '',
    freeText: ''
  }
}

function composeRuleDescription(structuredDescription: string, freeText: string): string {
  const normalizedStructuredDescription = structuredDescription.trim()
  const normalizedFreeText = freeText.trim()

  if (normalizedStructuredDescription && normalizedFreeText) {
    return `${normalizedStructuredDescription}; ${normalizedFreeText}`
  }

  return normalizedStructuredDescription || normalizedFreeText
}

function stripStructuredDescriptionPrefix(description: string | null | undefined, structuredDescription: string): string {
  const normalizedDescription = String(description || '').trim()
  const normalizedStructuredDescription = structuredDescription.trim()

  if (!normalizedDescription) return ''
  if (!normalizedStructuredDescription) return normalizedDescription
  if (normalizedDescription === normalizedStructuredDescription) return ''

  const prefix = `${normalizedStructuredDescription}; `
  if (normalizedDescription.startsWith(prefix)) {
    return normalizedDescription.slice(prefix.length).trim()
  }

  return ''
}

function hasLegacyIfAuthoringFields(node: IfNode | null | undefined): boolean {
  if (!node) return false

  return Boolean(
    node.reference ||
    node.condition ||
    node.value ||
    node.returnValue ||
    node.if ||
    node.else
  )
}

const FUTURE_REFERENCE = 'Future'

type ConditionLogicalOperator = 'AND' | 'OR'

interface ConditionEditorClause {
  kind: 'clause'
  reference: string
  condition: string
  values: string[]
  from: string
  to: string
  /** Selected composite option fragments keyed by an inclusive zero-based range (`start-end`). */
  positionValues: Record<string, string[]>
}

interface ConditionEditorGroup {
  kind: 'group'
  operator: ConditionLogicalOperator
  items: ConditionEditorItem[]
  origin?: 'explicit' | 'implicit'
}

type ConditionEditorItem = ConditionEditorClause | ConditionEditorGroup

function createEmptyConditionEditorClause(): ConditionEditorClause {
  return {
    kind: 'clause',
    reference: '',
    condition: '==',
    values: [],
    from: '',
    to: '',
    positionValues: {}
  }
}

function createEmptyConditionEditorGroup(
  operator: ConditionLogicalOperator = 'AND',
  origin: 'explicit' | 'implicit' = 'explicit'
): ConditionEditorGroup {
  return {
    kind: 'group',
    operator,
    origin,
    items: [createEmptyConditionEditorClause()]
  }
}

function hasFutureDescriptionTag(description: string | null | undefined): boolean {
  if (!description) return false
  return /(^|;\s*)future\s*$/i.test(description.trim()) || /^future$/i.test(description.trim())
}

function normalizeClauseValues(values: string[]): string[] {
  const normalized = values
    .map(value => value.trim())
    .filter(Boolean)

  return Array.from(new Set(normalized))
}

function isBetweenConditionClause(clause: Pick<ConditionEditorClause, 'condition'>): boolean {
  return clause.condition === 'between'
}

function isConditionEditorClauseComplete(clause: ConditionEditorClause): boolean {
  if (!clause.reference || !clause.condition) return false

  if (isBetweenConditionClause(clause)) {
    return Boolean(clause.from && clause.to)
  }

  return normalizeClauseValues(clause.values).length > 0
    || Object.values(clause.positionValues).some(values => normalizeClauseValues(values).length > 0)
}

function splitPositionableValues(values: Record<string, string> | null | undefined): Record<string, string[]> {
  const positions = Object.entries(values || {})
    .map(([key, value]) => ({ position: Number(key), value: String(value || '') }))
    .filter(entry => Number.isInteger(entry.position) && entry.position >= 0 && entry.value)
    .sort((left, right) => left.position - right.position)
  const result: Record<string, string[]> = {}
  let segment: Array<{ position: number; value: string }> = []

  const commit = () => {
    if (segment.length === 0) return
    const key = `${segment[0].position}-${segment[segment.length - 1].position}`
    result[key] = [segment.map(entry => entry.value).join('')]
    segment = []
  }

  positions.forEach((entry) => {
    if (segment.length > 0 && entry.position !== segment[segment.length - 1].position + 1) commit()
    segment.push(entry)
  })
  commit()
  return result
}

function isConditionEditorItemComplete(item: ConditionEditorItem): boolean {
  if (item.kind === 'clause') {
    return isConditionEditorClauseComplete(item)
  }

  return item.items.length > 0 && item.items.every(child => isConditionEditorItemComplete(child))
}

function groupConditionEntriesToEditorClauses(
  entries: ConditionEntry[],
  operator: ConditionLogicalOperator
): ConditionEditorClause[] {
  const clauses: ConditionEditorClause[] = []
  const clauseIndexByKey = new Map<string, number>()

  entries.forEach((entry, index) => {
    const reference = entry.reference || ''
    const condition = entry.condition || ''
    const value = entry.value || ''
    const from = entry.from || ''
    const to = entry.to || ''
    const positionValues = splitPositionableValues(entry.values)
    const isPositionable = Object.keys(positionValues).length > 0
    const canMergeValues = Boolean(reference && condition)
      && condition !== 'between'
      && (operator === 'OR' || isExclusionOperator(condition))
    const key = canMergeValues ? `${reference}|||${condition}|||${isPositionable ? 'positionable' : 'value'}` : `entry-${index}`
    const existingIndex = clauseIndexByKey.get(key)

    if (existingIndex === undefined) {
      clauses.push({
        kind: 'clause',
        reference,
        condition,
        values: value ? normalizeClauseValues([value]) : [],
        from,
        to,
        positionValues,
      })
      clauseIndexByKey.set(key, clauses.length - 1)
      return
    }

    if (!value && !isPositionable) return

    const existingClause = clauses[existingIndex]
    clauses[existingIndex] = {
      ...existingClause,
      values: value ? normalizeClauseValues([...existingClause.values, value]) : existingClause.values,
      positionValues: Object.entries(positionValues).reduce<Record<string, string[]>>((accumulator, [range, values]) => ({
        ...accumulator,
        [range]: normalizeClauseValues([...(accumulator[range] || []), ...values]),
      }), existingClause.positionValues),
    }
  })

  return clauses
}

function conditionGroupToEditorClause(group: ConditionGroup): ConditionEditorClause {
  const [clause] = groupConditionEntriesToEditorClauses(group.conditions || [], 'OR')
  return clause || createEmptyConditionEditorClause()
}

function conditionGroupToPositionableEditorClause(
  group: ConditionGroup | null | undefined
): ConditionEditorClause | null {
  if (!group) return null

  const entries: ConditionEntry[] = []
  const collectEntries = (current: ConditionGroup): boolean => {
    for (const entry of current.conditions || []) {
      if (!hasConditionEntryPositionValues(entry)) return false
      entries.push(entry)
    }
    return (current.groups || []).every(collectEntries)
  }

  if (!collectEntries(group) || entries.length === 0) return null
  const [firstEntry] = entries
  if (entries.some(entry => (
    entry.reference !== firstEntry.reference || entry.condition !== firstEntry.condition
  ))) return null

  const [clause] = groupConditionEntriesToEditorClauses(entries, 'OR')
  return clause || null
}

function conditionGroupToEditorGroup(
  group: ConditionGroup | null | undefined,
  fallbackOperator: ConditionLogicalOperator = 'AND'
): ConditionEditorGroup {
  if (!group || (group.logicalOperator !== 'AND' && group.logicalOperator !== 'OR')) {
    return createEmptyConditionEditorGroup(fallbackOperator)
  }

  const positionableClause = conditionGroupToPositionableEditorClause(group)
  if (positionableClause) {
    return {
      kind: 'group',
      operator: group.logicalOperator,
      origin: 'explicit',
      items: [positionableClause]
    }
  }

  const items: ConditionEditorItem[] = [
    ...groupConditionEntriesToEditorClauses(group.conditions || [], group.logicalOperator)
  ]

  ;(group.groups || []).forEach(subgroup => {
    const nestedPositionableClause = conditionGroupToPositionableEditorClause(subgroup)
    if (nestedPositionableClause) {
      items.push(nestedPositionableClause)
      return
    }
    if (isSimpleConditionGroup(subgroup)) {
      items.push(conditionGroupToEditorClause(subgroup))
      return
    }
    items.push(conditionGroupToEditorGroup(subgroup, subgroup.logicalOperator === 'OR' ? 'OR' : 'AND'))
  })

  return normalizeConditionEditorGroup({
    kind: 'group',
    operator: group.logicalOperator,
    origin: 'explicit',
    items: items.length > 0 ? items : [createEmptyConditionEditorClause()]
  })
}

function editorClauseToConditionGroup(clause: ConditionEditorClause): ConditionGroup {
  const normalizedValues = normalizeClauseValues(clause.values)
  const positionedGroups = Object.entries(clause.positionValues)
    .map<ConditionGroup | null>(([range, values]) => {
      const [start, end] = range.split('-').map(Number)
      const normalizedPositionValues = normalizeClauseValues(values)
      if (!Number.isInteger(start) || !Number.isInteger(end) || normalizedPositionValues.length === 0) return null
      return {
        logicalOperator: 'OR' as const,
        conditions: normalizedPositionValues.map((fragment) => ({
          reference: clause.reference || '',
          condition: '==',
          value: null,
          from: null,
          to: null,
          values: Object.fromEntries(
            Array.from({ length: end - start + 1 }, (_, offset) => [String(start + offset), fragment[offset] || ''])
              .filter(([, value]) => Boolean(value))
          ),
        })),
        groups: null,
      }
    })
    .filter((group): group is ConditionGroup => Boolean(group))

  if (positionedGroups.length > 0) {
    return {
      logicalOperator: 'AND',
      conditions: null,
      groups: positionedGroups,
    }
  }
  if (isBetweenConditionClause(clause)) {
    return {
      logicalOperator: 'OR',
      conditions: [{
        reference: clause.reference || '',
        condition: clause.condition || '',
        value: null,
        from: clause.from || '',
        to: clause.to || ''
      }],
      groups: null
    }
  }

  const conditions = normalizedValues.length > 0
    ? normalizedValues.map(value => ({
        reference: clause.reference || '',
        condition: clause.condition || '',
        value,
        from: null,
        to: null
      }))
    : [{
        reference: clause.reference || '',
        condition: clause.condition || '',
        value: '',
        from: null,
        to: null
      }]

  return {
    // "not D or C" means neither, so excluded values join with AND; every other operator keeps OR.
    logicalOperator: conditions.length > 1 && isExclusionOperator(clause.condition) ? 'AND' : 'OR',
    conditions,
    groups: null
  }
}

function editorGroupToConditionGroup(group: ConditionEditorGroup): ConditionGroup {
  const groups = group.items.map(item =>
    item.kind === 'clause' ? editorClauseToConditionGroup(item) : editorGroupToConditionGroup(item)
  )

  return {
    logicalOperator: group.operator,
    conditions: null,
    groups: groups.length > 0 ? groups : null
  }
}

function normalizeConditionEditorGroup(group: ConditionEditorGroup): ConditionEditorGroup {
  const normalizedItems: ConditionEditorItem[] = []

  group.items.forEach(item => {
    if (item.kind === 'clause') {
      normalizedItems.push({
        ...item,
        values: normalizeClauseValues(item.values),
        positionValues: Object.fromEntries(
          Object.entries(item.positionValues).map(([range, values]) => [range, normalizeClauseValues(values)])
        ),
        from: item.from.trim(),
        to: item.to.trim()
      })
      return
    }

    const normalizedGroup = normalizeConditionEditorGroup(item)

    if (normalizedGroup.items.length === 1) {
      normalizedItems.push(normalizedGroup.items[0])
      return
    }

    normalizedItems.push(normalizedGroup)
  })

  return {
    ...group,
    items: normalizedItems.length > 0 ? normalizedItems : [createEmptyConditionEditorClause()]
  }
}

function normalizeLegacyCompositePatterns(
  group: ConditionEditorGroup,
  codeItemsData: RuleCodeItemData[]
): { group: ConditionEditorGroup; changed: boolean } {
  let changed = false
  const items = group.items.map<ConditionEditorItem>((item) => {
    if (item.kind === 'group') {
      const normalized = normalizeLegacyCompositePatterns(item, codeItemsData)
      changed = changed || normalized.changed
      return normalized.group
    }

    if (Object.keys(item.positionValues).length > 0 || !item.values.some(value => value.includes('*'))) return item
    const referenceItem = codeItemsData.find(candidate => (
      normalizeCodeItemReference(candidate.code) === normalizeCodeItemReference(item.reference)
    ))
    if (!referenceItem?.composite || !referenceItem.subgroups?.length || item.condition !== '==') return item

    const positionValues: Record<string, string[]> = {}
    referenceItem.subgroups.forEach(subgroup => {
      const fragments = normalizeClauseValues(item.values.flatMap(pattern => {
        const fragment = pattern.slice(subgroup.start, subgroup.end + 1)
        return fragment.length === subgroup.end - subgroup.start + 1 && !fragment.includes('*') ? [fragment] : []
      }))
      if (fragments.length > 0) positionValues[`${subgroup.start}-${subgroup.end}`] = fragments
    })
    if (Object.keys(positionValues).length === 0) return item

    changed = true
    return { ...item, values: [], positionValues }
  })

  return { group: { ...group, items }, changed }
}

function hasConditionEditorBranches(group: ConditionEditorGroup): boolean {
  return group.items.some(item => item.kind === 'group')
}

function expandConditionEditorItemForOperator(
  item: ConditionEditorItem,
  operator: ConditionLogicalOperator
): ConditionEditorItem[] {
  if (item.kind === 'group' && item.operator === operator) {
    return item.items
  }

  return [item]
}

function mergeConditionEditorItemsWithOperator(
  leftItem: ConditionEditorItem,
  rightItem: ConditionEditorItem,
  operator: ConditionLogicalOperator
): ConditionEditorGroup {
  return normalizeConditionEditorGroup({
    kind: 'group',
    operator,
    origin: 'implicit',
    items: [
      ...expandConditionEditorItemForOperator(leftItem, operator),
      ...expandConditionEditorItemForOperator(rightItem, operator)
    ]
  })
}

interface ConditionsFormProps {
  rule: IfNode
  onChange: (rule: IfNode) => void
  conditions: Condition[]
  availableReferences: string[]
  codeItemsData?: RuleCodeItemData[]
}

interface ConditionOperatorToggleProps {
  value: ConditionLogicalOperator
  onChange: (value: ConditionLogicalOperator) => void
  compact?: boolean
}

const ConditionOperatorToggle: React.FC<ConditionOperatorToggleProps> = ({
  value,
  onChange,
  compact = false
}) => {
  return (
    <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5">
      {(['AND', 'OR'] as const).map(operator => (
        <button
          key={operator}
          type="button"
          onClick={() => onChange(operator)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            compact ? 'min-w-[44px]' : ''
          } ${
            value === operator
              ? 'bg-[#1D5176] text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {operator}
        </button>
      ))}
    </div>
  )
}

interface ConditionJoinToggleProps {
  value: ConditionLogicalOperator
  onChange: (value: ConditionLogicalOperator) => void
}

const ConditionJoinToggle: React.FC<ConditionJoinToggleProps> = ({ value, onChange }) => {
  return (
    <div className="inline-flex rounded-full border border-[#D7E6F5] bg-white p-0.5 shadow-sm">
      {(['AND', 'OR'] as const).map(operator => (
        <button
          key={operator}
          type="button"
          onClick={() => onChange(operator)}
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
            value === operator
              ? 'bg-[#1D5176] text-white'
              : 'text-[#1D5176] hover:bg-blue-50'
          }`}
        >
          {operator}
        </button>
      ))}
    </div>
  )
}

interface ConditionClauseEditorProps {
  clause: ConditionEditorClause
  onChange: (clause: ConditionEditorClause) => void
  onRemove: () => void
  onCreateBranch?: () => void
  branchLabel?: string
  conditions: Condition[]
  availableReferences: string[]
  codeItemsData?: RuleCodeItemData[]
}

const ConditionClauseEditor: React.FC<ConditionClauseEditorProps> = ({
  clause,
  onChange,
  onRemove,
  onCreateBranch,
  branchLabel,
  conditions,
  availableReferences,
  codeItemsData = []
}) => {
  const referenceCodeItem = useMemo(
    () => codeItemsData.find(item => normalizeCodeItemReference(item.code) === normalizeCodeItemReference(clause.reference)),
    [clause.reference, codeItemsData]
  )
  const compositeReference = Boolean(referenceCodeItem?.composite && referenceCodeItem.subgroups?.length)
  const referenceOptions = useMemo(() => {
    if (!clause.reference) return []
    return referenceCodeItem?.options || []
  }, [clause.reference, referenceCodeItem])

  const isBetween = isBetweenConditionClause(clause)
  const valuesDisabled = !clause.reference || (referenceOptions.length === 0 && clause.values.length === 0)
  const rangeValuesDisabled = !clause.reference || (referenceOptions.length === 0 && !clause.from && !clause.to)
  const canCreateBranchAction = Boolean(onCreateBranch) && isConditionEditorClauseComplete(clause)

  return (
    <div className="relative rounded-md border border-gray-200 bg-white p-2.5 space-y-1.5">
      <div className="absolute right-2 top-2 flex items-center gap-1">
        {onCreateBranch && branchLabel ? (
          <button
            type="button"
            onClick={onCreateBranch}
            disabled={!canCreateBranchAction}
            className={`inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] font-semibold transition-colors ${
              canCreateBranchAction
                ? 'text-[#1D5176] hover:bg-blue-50 hover:text-[#1D74D8]'
                : 'cursor-not-allowed text-gray-300'
            }`}
            title={`Create ${branchLabel} branch`}
            aria-label={`Create ${branchLabel} branch`}
          >
            <span>{branchLabel}</span>
            <ArrowRight className="h-3 w-3" />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-[#B3261E] transition-colors hover:bg-red-50"
          title="Remove condition"
          aria-label="Remove condition"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-[minmax(100px,1.1fr)_minmax(88px,0.9fr)_minmax(140px,1.5fr)] gap-1.5 pr-20">
        <div>
          <label className="block text-[11px] text-gray-500 mb-0.5">Reference</label>
          <ReferenceSelector
            value={clause.reference}
            onChange={(reference) => onChange({
              ...clause,
              reference,
              condition: '==',
              values: [],
              from: '',
              to: '',
              positionValues: {},
            })}
            availableReferences={availableReferences}
            compact
          />
        </div>
        <div>
          <label className="block text-[11px] text-gray-500 mb-0.5">Condition</label>
            <ConditionSelector
            value={compositeReference ? '==' : clause.condition}
            onChange={(condition) => onChange({
              ...clause,
              condition,
              values: condition === 'between' ? [] : clause.values,
              from: condition === 'between' ? '' : '',
              to: condition === 'between' ? '' : ''
            })}
            conditions={compositeReference ? [{ value: '==', displayName: 'equals' }] : conditions}
            disabled={!clause.reference || compositeReference}
            compact
          />
        </div>
        <div className={compositeReference ? 'col-span-3' : undefined}>
          <label className="block text-[11px] text-gray-500 mb-0.5">{compositeReference ? 'Values per sub-position' : isBetween ? 'Range' : 'Values'}</label>
          {compositeReference ? (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(referenceCodeItem?.subgroups || []).map((subgroup) => {
                const rangeKey = `${subgroup.start}-${subgroup.end}`
                return (
                  <div key={rangeKey}>
                    <label className="mb-0.5 block text-[10px] font-medium text-gray-600">
                      {subgroup.name} · Pos {subgroup.start + 1}{subgroup.end === subgroup.start ? '' : `-${subgroup.end + 1}`}
                    </label>
                    <MultiValueSelector
                      values={clause.positionValues[rangeKey] || []}
                      onChange={(values) => onChange({
                        ...clause,
                        condition: '==',
                        values: [],
                        from: '',
                        to: '',
                        positionValues: {
                          ...clause.positionValues,
                          [rangeKey]: normalizeClauseValues(values),
                        },
                      })}
                      options={normalizeClauseValues(subgroup.options)}
                      disabled={false}
                      placeholder="Any"
                      compact
                    />
                  </div>
                )
              })}
            </div>
          ) : isBetween ? (
            <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-1.5">
              <ValueSelector
                value={clause.from}
                onChange={(from) => onChange({
                  ...clause,
                  from
                })}
                options={referenceOptions}
                disabled={rangeValuesDisabled}
                placeholder="From"
                allowFreeTextWhenNoOptions={false}
                compact
              />
              <span className="text-xs text-gray-400" aria-hidden="true">-</span>
              <ValueSelector
                value={clause.to}
                onChange={(to) => onChange({
                  ...clause,
                  to
                })}
                options={referenceOptions}
                disabled={rangeValuesDisabled}
                placeholder="To"
                allowFreeTextWhenNoOptions={false}
                compact
              />
            </div>
          ) : (
            <MultiValueSelector
              values={clause.values}
              onChange={(values) => onChange({
                ...clause,
                values: normalizeClauseValues(values)
              })}
              options={referenceOptions}
              disabled={valuesDisabled}
              placeholder={
                !clause.reference
                  ? 'Select reference first'
                  : 'No options available'
              }
              compact
            />
          )}
        </div>
      </div>

      <div className="flex items-start justify-between gap-3 pr-24">
        <div className="text-[10px] text-gray-500">
          {compositeReference
            ? 'Leave a sub-position empty to evaluate it as Any. Multiple selected values are evaluated as OR.'
            : isBetween
            ? 'Select the inclusive lower and upper bounds for this range.'
            : 'Multiple values in this row are evaluated as OR.'}
          {!compositeReference && clause.reference && referenceOptions.length === 0 && clause.values.length === 0 && !clause.from && !clause.to ? (
            <span className="block text-amber-600 mt-0.5">No options registered for this Code item yet.</span>
          ) : null}
        </div>
      </div>
    </div>
  )
}

interface ConditionGroupEditorProps {
  group: ConditionEditorGroup
  onChange: (group: ConditionEditorGroup) => void
  onRemove?: () => void
  conditions: Condition[]
  availableReferences: string[]
  codeItemsData?: RuleCodeItemData[]
  depth?: number
  title?: string
  secondaryAction?: React.ReactNode
}

const ConditionGroupEditor: React.FC<ConditionGroupEditorProps> = ({
  group,
  onChange,
  onRemove,
  conditions,
  availableReferences,
  codeItemsData = [],
  depth = 0,
  title,
  secondaryAction
}) => {
  const [expandedJoinIndexes, setExpandedJoinIndexes] = useState<number[]>([])

  const commitGroupChange = useCallback((nextGroup: ConditionEditorGroup) => {
    onChange(normalizeConditionEditorGroup(nextGroup))
  }, [onChange])

  const updateItem = (index: number, nextItem: ConditionEditorItem) => {
    if (nextItem.kind === 'group') {
      if (nextItem.items.length === 1) {
        commitGroupChange({
          ...group,
          items: group.items.map((item, itemIndex) => itemIndex === index ? nextItem.items[0] : item)
        })
        return
      }

      if (nextItem.origin === 'implicit' && nextItem.operator === group.operator) {
        commitGroupChange({
          ...group,
          items: [
            ...group.items.slice(0, index),
            ...nextItem.items,
            ...group.items.slice(index + 1)
          ]
        })
        return
      }
    }

    commitGroupChange({
      ...group,
      items: group.items.map((item, itemIndex) => itemIndex === index ? nextItem : item)
    })
  }

  const removeItem = (index: number) => {
    const nextItems = group.items.filter((_, itemIndex) => itemIndex !== index)
    commitGroupChange({
      ...group,
      items: nextItems.length > 0 ? nextItems : [createEmptyConditionEditorClause()]
    })
  }

  const appendConditionAtCurrentLevel = () => {
    commitGroupChange({
      ...group,
      items: [...group.items, createEmptyConditionEditorClause()]
    })
  }

  const updateJoinOperator = (index: number, operator: ConditionLogicalOperator) => {
    if (operator === group.operator) return

    const leftItem = group.items[index]
    const rightItem = group.items[index + 1]

    if (!leftItem || !rightItem) return

    if (group.items.length === 2) {
      commitGroupChange({
        ...group,
        operator
      })
      return
    }

    const mergedGroup = mergeConditionEditorItemsWithOperator(leftItem, rightItem, operator)

    setExpandedJoinIndexes(previous => previous.filter(itemIndex => itemIndex !== index))

    commitGroupChange({
      ...group,
      items: [
        ...group.items.slice(0, index),
        mergedGroup,
        ...group.items.slice(index + 2)
      ]
    })
  }

  const createOppositeOperatorGroupFromItem = (index: number) => {
    const oppositeOperator: ConditionLogicalOperator = group.operator === 'AND' ? 'OR' : 'AND'
    const items = [...group.items]
    const currentItem = items[index]

    if (!currentItem) {
      commitGroupChange({
        ...group,
        items: [createEmptyConditionEditorGroup(oppositeOperator)]
      })
      return
    }

    if (!isConditionEditorItemComplete(currentItem)) return

    if (currentItem.kind === 'group' && currentItem.operator === oppositeOperator) {
      items[index] = {
        ...currentItem,
        items: [...currentItem.items, createEmptyConditionEditorClause()]
      }
      commitGroupChange({
        ...group,
        items
      })
      return
    }

    items[index] = {
      kind: 'group',
      operator: oppositeOperator,
      origin: 'explicit',
      items: [currentItem, createEmptyConditionEditorClause()]
    }

    commitGroupChange({
      ...group,
      items
    })
  }

  const groupTitle = depth === 0
    ? (title || (group.operator === 'AND' ? 'All Conditions Must Match' : 'Any Condition Can Match'))
    : `${group.operator} Branch`

  const primaryAddLabel = group.operator === 'AND'
    ? 'Add AND condition'
    : 'Add OR option'

  const clauseBranchLabel = group.operator === 'AND' ? 'OR' : 'AND'
  const canAppendCondition = group.items.every(item => isConditionEditorItemComplete(item))

  const fieldsetClassName = depth === 0
    ? 'border-l-2 border-[#0096E0] pl-3 pt-1 pb-2'
    : 'rounded-lg border border-gray-200 bg-white px-3 pt-1 pb-3 shadow-sm'

  return (
    <fieldset className={fieldsetClassName}>
      <legend className="px-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#0096E0]">{groupTitle}</span>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-[#B3261E] transition-colors hover:bg-red-50"
              title="Remove group"
              aria-label="Remove group"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </legend>

      <div className="space-y-3">
        {depth === 0 ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Main operator</label>
              <ConditionOperatorToggle
                value={group.operator}
                onChange={(operator) => commitGroupChange({ ...group, operator })}
              />
            </div>
            <p className="text-[11px] text-gray-500 max-w-[190px] text-right">
              {group.operator === 'AND'
                ? 'Use this when every condition in the root flow must match.'
                : 'Use this when any one condition in the root flow can match.'}
            </p>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2">
            <p className="text-[11px] text-gray-600">
              {group.operator === 'AND'
                ? 'All rows in this branch must match.'
                : 'Any row in this branch can match.'}
            </p>
            <ConditionOperatorToggle
              value={group.operator}
              onChange={(operator) => commitGroupChange({ ...group, operator })}
              compact
            />
          </div>
        )}

        {group.items.map((item, index) => (
          <React.Fragment key={`${item.kind}-${depth}-${index}`}>
            <div className="space-y-2">
              {item.kind === 'clause' ? (
                <ConditionClauseEditor
                  clause={item}
                  onChange={(nextClause) => updateItem(index, nextClause)}
                  onRemove={() => removeItem(index)}
                  onCreateBranch={() => createOppositeOperatorGroupFromItem(index)}
                  branchLabel={clauseBranchLabel}
                  conditions={conditions}
                  availableReferences={availableReferences}
                  codeItemsData={codeItemsData}
                />
              ) : (
                <div className="flex min-w-max items-start gap-3 pr-2">
                  <div className="pt-11">
                    <div className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#D7E6F5] bg-[#F5FAFF] px-3 py-1 text-xs font-medium text-[#1D5176]">
                      <span>{item.operator} branch</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                  <div style={{ minWidth: 340 + (depth * 72) }}>
                    <ConditionGroupEditor
                      group={item}
                      onChange={(nextGroup) => updateItem(index, nextGroup)}
                      onRemove={() => removeItem(index)}
                      conditions={conditions}
                      availableReferences={availableReferences}
                      codeItemsData={codeItemsData}
                      depth={depth + 1}
                    />
                  </div>
                </div>
              )}
            </div>

            {index < group.items.length - 1 ? (
              <div className="flex min-w-max items-center gap-3 pl-2 pr-2">
                {isConditionEditorItemComplete(item) && isConditionEditorItemComplete(group.items[index + 1]) ? (
                  expandedJoinIndexes.includes(index) ? (
                    <>
                      <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Join</span>
                      <ConditionJoinToggle
                        value={group.operator}
                        onChange={(operator) => updateJoinOperator(index, operator)}
                      />
                      <button
                        type="button"
                        onClick={() => setExpandedJoinIndexes(previous => previous.filter(itemIndex => itemIndex !== index))}
                        className="text-[10px] font-medium text-gray-400 hover:text-[#1D5176]"
                      >
                        Done
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setExpandedJoinIndexes(previous => previous.includes(index) ? previous : [...previous, index])}
                      className="text-[11px] font-medium text-[#1D5176] transition-colors hover:text-[#1D74D8]"
                    >
                      Manage individually
                    </button>
                  )
                ) : null}
              </div>
            ) : null}
          </React.Fragment>
        ))}

        <div className="flex min-w-max items-center gap-3 pr-2 pt-1">
          <button
            type="button"
            onClick={appendConditionAtCurrentLevel}
            disabled={!canAppendCondition}
            className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-dashed px-3 py-2 text-sm font-medium transition-colors ${
              canAppendCondition
                ? 'border-[#1D74D8] bg-white text-[#1D5176] hover:bg-blue-50 hover:text-[#1D74D8]'
                : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
            }`}
          >
            <Plus className="h-4 w-4" />
            <span>{primaryAddLabel}</span>
          </button>
          {depth === 0 ? secondaryAction : null}
        </div>
      </div>
    </fieldset>
  )
}

const ConditionsForm: React.FC<ConditionsFormProps> = ({
  rule,
  onChange,
  conditions,
  availableReferences,
  codeItemsData = []
}) => {
  const normalizedTriggerEditor = useMemo(
    () => normalizeLegacyCompositePatterns(conditionGroupToEditorGroup(rule.conditionGroup), codeItemsData),
    [codeItemsData, rule.conditionGroup]
  )
  const triggerEditorGroup = normalizedTriggerEditor.group

  const normalizedConsequenceEditors = useMemo(
    () => (rule.consequenceGroups || []).map(group => (
      normalizeLegacyCompositePatterns(conditionGroupToEditorGroup(group), codeItemsData)
    )),
    [codeItemsData, rule.consequenceGroups]
  )
  const consequenceEditorGroups = normalizedConsequenceEditors.map(item => item.group)

  useEffect(() => {
    if (!normalizedTriggerEditor.changed && !normalizedConsequenceEditors.some(item => item.changed)) return
    onChange({
      ...rule,
      conditionGroup: editorGroupToConditionGroup(normalizeConditionEditorGroup(triggerEditorGroup)),
      consequenceGroups: consequenceEditorGroups.map(group => editorGroupToConditionGroup(normalizeConditionEditorGroup(group))),
    })
  }, [consequenceEditorGroups, normalizedConsequenceEditors, normalizedTriggerEditor.changed, onChange, rule, triggerEditorGroup])

  const applyConsequenceEditorGroups = useCallback((nextGroups: ConditionEditorGroup[]) => {
    onChange({
      ...rule,
      consequenceGroups: nextGroups.map(group => editorGroupToConditionGroup(normalizeConditionEditorGroup(group)))
    })
  }, [onChange, rule])

  const applyEditorGroup = useCallback((nextGroup: ConditionEditorGroup) => {
    const normalizedGroup = normalizeConditionEditorGroup(nextGroup)
    const conditionGroup = editorGroupToConditionGroup(normalizedGroup)
    onChange({
      ...rule,
      conditionGroup: hasConfiguredConditionGroup(conditionGroup) ? conditionGroup : null
    })
  }, [onChange, rule])

  const canAddConsequence = Boolean(rule.conditionGroup && validateConditionGroup(rule.conditionGroup))
    && consequenceEditorGroups.every(group => validateConditionGroup(editorGroupToConditionGroup(group)))

  const addConsequence = useCallback(() => {
    if (!canAddConsequence) return
    applyConsequenceEditorGroups([
      ...consequenceEditorGroups,
      createEmptyConditionEditorGroup('AND')
    ])
  }, [applyConsequenceEditorGroups, canAddConsequence, consequenceEditorGroups])

  const updateConsequence = useCallback((index: number, nextGroup: ConditionEditorGroup) => {
    applyConsequenceEditorGroups(
      consequenceEditorGroups.map((group, groupIndex) => groupIndex === index ? nextGroup : group)
    )
  }, [applyConsequenceEditorGroups, consequenceEditorGroups])

  const removeConsequence = useCallback((index: number) => {
    applyConsequenceEditorGroups(
      consequenceEditorGroups.filter((_, groupIndex) => groupIndex !== index)
    )
  }, [applyConsequenceEditorGroups, consequenceEditorGroups])

  const hasConsequenceGroups = consequenceEditorGroups.length > 0
  const addConsequenceButtonClassName = `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-dashed px-3 py-2 text-sm font-medium transition-colors ${
    canAddConsequence
      ? 'border-[#1D74D8] bg-white text-[#1D5176] hover:bg-blue-50 hover:text-[#1D74D8]'
      : 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
  }`

  const addConsequenceButton = (
    <button
      type="button"
      onClick={addConsequence}
      disabled={!canAddConsequence}
      className={addConsequenceButtonClassName}
    >
      <Plus className="h-4 w-4" />
      <span>Add consequence</span>
    </button>
  )

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        Build the rule in two steps: first define the <strong>IF</strong> trigger, then optionally add <strong>THEN</strong> consequence groups. Consequences do not gate each other; they are enforced in parallel when the trigger matches.
      </div>

      <div className="space-y-2">
        <div>
          <div className="text-sm font-semibold text-[#1D5176]">IF</div>
          <p className="mt-1 text-xs text-gray-500">
            Author the trigger condition. You can branch with AND/OR and values come from the referenced code item options.
          </p>
        </div>
        <div className="overflow-x-auto pb-2">
          <div className="w-full pr-2">
            <ConditionGroupEditor
              group={triggerEditorGroup}
              onChange={applyEditorGroup}
              conditions={conditions}
              availableReferences={availableReferences}
              codeItemsData={codeItemsData}
              title="Trigger"
              secondaryAction={!hasConsequenceGroups ? addConsequenceButton : null}
            />
          </div>
        </div>
      </div>

      {hasConsequenceGroups ? (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-[#1D5176]">THEN</div>
            <p className="mt-1 text-xs text-gray-500">
              Add structured consequence groups when the trigger should enforce additional parallel constraints.
            </p>
          </div>

          {consequenceEditorGroups.map((group, index) => (
            <div key={`consequence-group-${index}`} className="overflow-x-auto pb-2">
              <div className="w-full pr-2">
                <ConditionGroupEditor
                  group={group}
                  onChange={(nextGroup) => updateConsequence(index, nextGroup)}
                  onRemove={() => removeConsequence(index)}
                  conditions={conditions}
                  availableReferences={availableReferences}
                  codeItemsData={codeItemsData}
                  title={`Consequence ${index + 1}`}
                />
              </div>
            </div>
          ))}

          <div className="overflow-x-auto pb-2">
            <div className="w-full pr-2">
              {addConsequenceButton}
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-md border border-gray-200 bg-white px-3 py-3">
        <label className="flex items-start gap-3 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={Boolean(rule.future)}
            onChange={(event) => onChange({
              ...rule,
              future: event.target.checked
            })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#1D5176] focus:ring-[#1D5176]"
          />
          <span>
            Append <strong>{FUTURE_REFERENCE}</strong> at the end of the rule description.
          </span>
        </label>
      </div>
    </div>
  )
}

interface FreeTextRuleSectionProps {
  value: string
  onChange: (value: string) => void
}

const FreeTextRuleSection: React.FC<FreeTextRuleSectionProps> = ({ value, onChange }) => {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-3 space-y-2">
      <div>
        <label className="block text-sm font-medium text-gray-700">Additional Text Rule</label>
        <p className="mt-1 text-xs text-gray-500">
          Use this for special instructions that do not depend on a referenced Code item.
        </p>
      </div>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        placeholder='Example: Available for factory-installed options only'
        className="w-full resize-y rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-[#1D74D8] focus:ring-1 focus:ring-[#1D74D8]"
      />
    </div>
  )
}

// -----------------------------
// Main Drawer Component
// -----------------------------
export const ConditionConfigDrawer: React.FC<ConditionConfigDrawerProps> = ({
  open,
  onClose,
  onApply,
  initialConfig,
  availableReferences = [],
  currentCodeItem,
  codeItemsData = [],
  ruleValidationContext,
  notice
}) => {
  const [activeTab, setActiveTab] = useState<'conditions' | 'while' | 'if'>('conditions')
  const [conditions, setConditions] = useState<Condition[]>([])
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const conditionsTabConditions = useMemo(
    () => mergeConditionsTabOperators(conditions),
    [conditions]
  )

  const [ifRule, setIfRule] = useState<IfNode>(createEmptyIfRule)
  const [freeText, setFreeText] = useState('')
  useEffect(() => {
    let cancelled = false
    const loadConditions = async () => {
      setLoading(true)
      try {
        const data = await fetchConditions()
        if (!cancelled) setConditions(data)
      } catch (err) {
        console.error('Failed to load conditions:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (open) loadConditions()
    return () => { cancelled = true }
  }, [open])

  const prevOpenRef = React.useRef(false)
  const pendingFreeTextDerivationRef = React.useRef<string | null>(null)

  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false
      pendingFreeTextDerivationRef.current = null
      return
    }

    if (prevOpenRef.current) return
    prevOpenRef.current = true

    setFreeText(initialConfig?.freeText || '')
    pendingFreeTextDerivationRef.current = null
    setActiveTab('conditions')

    if (initialConfig?.rule) {
      if (initialConfig.rule.type === 'while') {
        setIfRule(createEmptyIfRule())
      } else if (initialConfig.rule.type === 'if') {
        const incomingIfRule = initialConfig.rule as IfNode
        const hasFuture = Boolean(incomingIfRule.future) || hasFutureDescriptionTag(incomingIfRule.description)
        const shouldPreserveStructuredRule = !hasLegacyIfAuthoringFields(incomingIfRule)
        setIfRule({
          ...(shouldPreserveStructuredRule ? incomingIfRule : createEmptyIfRule()),
          future: hasFuture,
          conditionGroup: shouldPreserveStructuredRule ? (incomingIfRule.conditionGroup ?? null) : null,
          consequenceGroups: shouldPreserveStructuredRule ? normalizeConsequenceGroups(incomingIfRule.consequenceGroups) : null,
        })
        if (
          shouldPreserveStructuredRule &&
          !initialConfig?.freeText &&
          (initialConfig.description || initialConfig.rule.description)
        ) {
          pendingFreeTextDerivationRef.current = initialConfig.description || initialConfig.rule.description || ''
        }
      } else if (initialConfig.rule.type === 'text') {
        setIfRule(createEmptyIfRule())
        setFreeText(initialConfig.freeText || initialConfig.description || initialConfig.rule.description || '')
      }
    } else if (initialConfig?.type === 'always') {
      setIfRule(createEmptyIfRule())
    } else {
      setIfRule(createEmptyIfRule())
    }
  }, [open, initialConfig])

  const handleIfRuleChange = useCallback((rule: IfNode) => {
    setIfRule(rule)
  }, [])

  const resolvedConditionsRule = useMemo<IfNode>(() => {
    return {
      ...ifRule,
      returnValue: ifRule.returnValue ?? null,
      if: ifRule.if ?? null,
      else: ifRule.else ?? null,
      future: Boolean(ifRule.future),
      conditionGroup: ifRule.conditionGroup ?? null,
      consequenceGroups: normalizeConsequenceGroups(ifRule.consequenceGroups)
    }
  }, [ifRule])

  const structuredDescription = useMemo(() => {
    return generateStructuredIfDescription(resolvedConditionsRule, conditionsTabConditions, codeItemsData)
  }, [codeItemsData, conditionsTabConditions, resolvedConditionsRule])

  useEffect(() => {
    if (!open) return
    if (loading) return
    if (!pendingFreeTextDerivationRef.current) return

    setFreeText(stripStructuredDescriptionPrefix(pendingFreeTextDerivationRef.current, structuredDescription))
    pendingFreeTextDerivationRef.current = null
  }, [loading, open, structuredDescription])

  const description = useMemo(() => {
    return composeRuleDescription(structuredDescription, freeText)
  }, [structuredDescription, freeText])

  const descriptionPreviewRule = useMemo(() => {
    const normalizedFreeText = freeText.trim()
    const hasConfiguredConsequences = hasConfiguredConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const hasValidTrigger = validateConditionGroup(resolvedConditionsRule.conditionGroup)
    const hasValidConsequences = validateConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const hasAnyStructuredConditionRule = hasConfiguredConditionGroup(resolvedConditionsRule.conditionGroup)
      || hasConfiguredConsequences
    const hasConfiguredConditionRule = Boolean(
      hasValidTrigger && (!hasConfiguredConsequences || hasValidConsequences)
    )

    if (hasConfiguredConditionRule || hasAnyStructuredConditionRule) {
      return {
        ...resolvedConditionsRule,
        description,

        freeText: normalizedFreeText || null
      }
    }

    if (resolvedConditionsRule.future) {
      return {
        ...resolvedConditionsRule,
        description,

        freeText: normalizedFreeText || null
      }
    }

    if (description) {
      return {
        ...createEmptyTextRule(),
        description,

        freeText: normalizedFreeText || null
      }
    }

    return null
  }, [description, freeText, resolvedConditionsRule])

  const targetRuleLabel = useMemo(() => {
    if (!ruleValidationContext) return null

    const targetGroup = ruleValidationContext.groups[ruleValidationContext.targetGroupIndex]
    const targetRow = targetGroup?.rows[ruleValidationContext.targetRowIndex]
    const targetCode = targetGroup?.codeItemPos.code || currentCodeItem || ''
    const targetValue = targetRow?.optionCode || ''

    if (!targetCode || !targetValue) return null

    return `${formatConditionReference(targetCode)} value ${targetValue}`
  }, [currentCodeItem, ruleValidationContext])

  const previewValidation = useMemo(() => {
    return validateRuleLogic(descriptionPreviewRule, ruleValidationContext)
  }, [descriptionPreviewRule, ruleValidationContext])

  const previewValidationError = previewValidation.errors[0] || null
  const previewValidationIssue = previewValidationError || previewValidation.warnings[0] || null
  // Every dead branch of a rule is worth marking, so warnings highlight together.
  const previewValidationHighlights = useMemo(() => (
    previewValidationError
      ? previewValidationError.localConditions || []
      : previewValidation.warnings.flatMap(warning => warning.localConditions || [])
  ), [previewValidationError, previewValidation.warnings])
  const previewValidationMessage = previewValidationIssue?.message || ''
  const previewValidationSummary = previewValidationIssue
    ? getRuleValidationIssueSummary(previewValidationIssue)
    : null
  const hasPreviewError = Boolean(previewValidationError)
  const hasPreviewWarning = !hasPreviewError && previewValidation.warnings.length > 0
  const descriptionPreviewContainerClassName = 'p-3 rounded border min-h-[60px] bg-gray-50 border-gray-200'

  const hasConditionBranches = useMemo(() => {
    const triggerHasBranches = hasConditionEditorBranches(conditionGroupToEditorGroup(resolvedConditionsRule.conditionGroup))
    const consequenceHasBranches = (resolvedConditionsRule.consequenceGroups || []).some(group =>
      hasConditionEditorBranches(conditionGroupToEditorGroup(group))
    )
    return triggerHasBranches || consequenceHasBranches
  }, [resolvedConditionsRule.conditionGroup, resolvedConditionsRule.consequenceGroups])

  const drawerWidth = hasConditionBranches ? '720px' : '560px'

  const handleShowPreviewValidationDetails = useCallback(async () => {
    if (!previewValidationError) return

    await showErrorModal({
      title: 'Rule Validation Details',
      description: previewValidationError.message,
      confirmText: 'OK',
    })
  }, [previewValidationError])

  const handleApply = useCallback(async () => {
    const normalizedFreeText = freeText.trim()
    const hasConfiguredConsequences = hasConfiguredConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const hasValidConsequences = validateConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const conditionsRule = {
      ...resolvedConditionsRule,
      conditionGroup: validateConditionGroup(resolvedConditionsRule.conditionGroup)
        ? resolvedConditionsRule.conditionGroup
        : null,
      consequenceGroups: validateConsequenceGroups(resolvedConditionsRule.consequenceGroups)
        ? normalizeConsequenceGroups(resolvedConditionsRule.consequenceGroups)
        : null,
      reference: null,
      condition: null,
      value: null,
      returnValue: null,
      if: null,
      else: null,
    }
    let nextType: RuleConfig['type'] = 'always'
    let nextRule: RuleConfig['rule'] = null

    if (conditionsRule.conditionGroup && (!hasConfiguredConsequences || hasValidConsequences)) {
      nextType = 'if'
      nextRule = {
        ...conditionsRule,
        description,

        freeText: normalizedFreeText || null
      }
    } else if (conditionsRule.future) {
      nextType = 'if'
      nextRule = {
        ...conditionsRule,
        description,

        freeText: normalizedFreeText || null
      }
    } else if (description) {
      nextType = 'text'
      nextRule = {
        ...createEmptyTextRule(),
        description,

        freeText: normalizedFreeText || null
      }
    }

    const ruleLogicValidation = validateRuleLogic(nextRule, ruleValidationContext)
    const ruleLogicError = ruleLogicValidation.errors[0] || null

    if (ruleLogicError) {
      await showErrorModal({
        title: 'Unable to Save Rule',
        description: ruleLogicError.message,
        confirmText: 'OK',
      })
      return
    }

    const ruleReferenceValidationIssue = nextRule
      ? validateRuleValueReferences(nextRule, codeItemsData)[0] || null
      : null

    if (ruleReferenceValidationIssue) {
      await showErrorModal({
        title: 'Unable to Save Rule',
        description: `Cannot save rule. Value ${ruleReferenceValidationIssue.missingValue} does not exist on code item ${ruleReferenceValidationIssue.referencedCodeItemCode}.`,
        confirmText: 'OK',
      })
      return
    }

    if (ruleLogicValidation.warnings.length > 0) {
      const confirmed = await showConfirmModal({
        title: ruleLogicValidation.warnings.every(warning => warning.type === 'numeric-operator')
          ? 'Numeric Comparison Warning'
          : 'Rule Warning',
        description: `${ruleLogicValidation.warnings.map(warning => warning.message).join(' ')} Do you want to proceed?`,
        confirmText: 'Proceed',
        cancelText: 'Cancel',
      })

      if (!confirmed) return
    }

    try {
      setApplying(true)
      await onApply({
        type: nextType,
        rule: nextRule,
        description: description || null,

        freeText: normalizedFreeText || null,
      })
      onClose()
    } catch (error) {
      console.error('Failed to apply condition config', error)
    } finally {
      setApplying(false)
    }
  }, [codeItemsData, description, freeText, onApply, onClose, resolvedConditionsRule, ruleValidationContext])

  const isValid = useMemo(() => {
    const hasValidTrigger = Boolean(
      resolvedConditionsRule.conditionGroup && validateConditionGroup(resolvedConditionsRule.conditionGroup)
    )
    const hasConfiguredConsequences = hasConfiguredConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const hasValidConsequences = validateConsequenceGroups(resolvedConditionsRule.consequenceGroups)
    const hasAnyStructuredContent = hasConfiguredConditionGroup(resolvedConditionsRule.conditionGroup)
      || hasConfiguredConsequences

    if (!hasAnyStructuredContent) {
      return true
    }

    if (!hasConfiguredConsequences) {
      return Boolean(hasValidTrigger)
    }

    return Boolean(hasValidTrigger && hasValidConsequences)
  }, [resolvedConditionsRule])

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          maxWidth: '96vw',
          boxSizing: 'border-box',
          transition: 'width 180ms ease'
        }
      }}
    >
      <div className="h-full flex flex-col bg-white">
        {/* Header */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
          <div className="flex min-w-0 items-center gap-2">
            <Typography variant="h6" className="font-semibold text-gray-900">
              Condition Config.
            </Typography>
            {targetRuleLabel ? (
              <span className="inline-flex max-w-[220px] items-center truncate rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#1D5176]">
                {targetRuleLabel}
              </span>
            ) : null}
          </div>
          <IconButton onClick={onClose} size="small" className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', px: 2 }}>
          <Tabs
            value={activeTab}
            onChange={() => setActiveTab('conditions')}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '13px', color: '#6B7280', minHeight: '44px', padding: '8px 16px' },
              '& .Mui-selected': { color: '#1D5176', fontWeight: 600 },
              '& .MuiTabs-indicator': { backgroundColor: '#1D5176', height: '2px' },
            }}
          >
            <Tab label="Conditions" value="conditions" />
          </Tabs>
        </Box>

        {notice ? (
          <div data-testid="condition-config-notice" className="border-b border-[#F6D79A] bg-[#FFF7E8] px-4 py-2 text-xs text-[#8A5A00]">
            {notice}
          </div>
        ) : null}

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-[#1D74D8] rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              <ConditionsForm
                rule={ifRule}
                onChange={handleIfRuleChange}
                conditions={conditionsTabConditions}
                availableReferences={availableReferences}
                codeItemsData={codeItemsData}
              />

              <div>
                <div className="mb-1 flex items-center gap-1.5">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  {previewValidationIssue ? (
                    <Tooltip title={previewValidationMessage}>
                      <span
                        className={`inline-flex h-4 w-4 items-center justify-center ${
                          hasPreviewError ? 'text-red-600' : 'text-amber-600'
                        }`}
                      >
                        <AlertCircle className="h-3.5 w-3.5" />
                      </span>
                    </Tooltip>
                  ) : null}
                </div>
                <div
                  className={descriptionPreviewContainerClassName}
                  data-rule-preview-state={hasPreviewError ? 'error' : hasPreviewWarning ? 'warning' : 'valid'}
                  data-testid="rule-description-preview"
                >
                  {descriptionPreviewRule ? (
                    <AvailabilityBadge
                      value={descriptionPreviewRule}

                      showConfigIcon={false}
                      conditionHighlights={previewValidationHighlights}
                      conditionHighlightVariant={hasPreviewWarning ? 'warning' : 'error'}
                    />
                  ) : (
                    <p className="text-sm text-gray-400 italic">Rule description</p>
                  )}
                </div>
                {previewValidationSummary ? (
                  <div className={`mt-1 flex items-center gap-2 text-xs ${hasPreviewError ? 'text-red-700' : 'text-amber-700'}`}>
                    <span>{previewValidationSummary}</span>
                    {previewValidationError ? (
                      <button
                        type="button"
                        onClick={() => void handleShowPreviewValidationDetails()}
                        className="font-medium underline underline-offset-2 hover:text-red-900"
                      >
                        Details
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <FreeTextRuleSection value={freeText} onChange={setFreeText} />

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex items-center justify-end gap-3 bg-white">
          <Button onClick={() => void handleApply()} disabled={!isValid || loading || applying || hasPreviewError}>
            Apply
          </Button>
        </div>
      </div>

    </Drawer>
  )
}

export default ConditionConfigDrawer
